import { ApolloError } from '@apollo/client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SortingRule } from 'react-table';
import { useIsNewSearch, useManualPagination } from '../../../../shared/hooks';
import { OrgTenantsContext } from '../../../../shared/providers/OrgTenantsProvider';
import { useGetTopDetectionEntitiesInsightsPaginated } from '../../../detections/queries';
import { DetectionSummaryInsights } from '../../types';
import { useGetTopDetectionsByDetectionGroupsPerEntity } from '@investigator/features/detections/queries/useGetDetectionsByDetectionGroupsPerEntity';
import {
  DetectionGroups,
  DetectionsByDetectionGroupPerEntity,
} from '@investigator/shared/generated/graphql';
import { useAgenticTriageSettings } from '../../hooks';
import moment from 'moment';

const DEFAULT_SORT: SortingRule<DetectionSummaryInsights>[] = [
  { id: 'max_severity', desc: true },
];

export interface EntityTableFilters {
  id: string;
  value: string;
}

export interface EntityTableContextType {
  // === DATA ===
  tableData: DetectionSummaryInsights[];
  totalItems: number;
  isLoading: boolean;
  error: ApolloError | undefined;

  // === PAGINATION ===
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  offset: number;
  onFetchData: (
    pageIndex: number,
    pageSize: number,
    sortBy: { id: string; desc?: boolean }[],
  ) => void;

  // === FILTERING ===
  filters: EntityTableFilters[] | null;
  setFilters: (filters: EntityTableFilters[] | null) => void;

  // === SORTING ===
  sortBy: { id: string; desc?: boolean }[];

  // === REFETCH ===
  refetchTable: () => Promise<unknown>;
}

// Export for testing purposes
export const EntityTableContextInstance = createContext<
  EntityTableContextType | undefined
>(undefined);

export interface EntityTableContextProps {
  children: React.ReactNode;
}

export const EntityTableContext: React.FC<EntityTableContextProps> = ({
  children,
}) => {
  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);
  const { agenticTriageEnabled } = useAgenticTriageSettings();

  // Local state
  const [initialFetch, setInitialFetch] = useState<boolean>(false);
  const [tableData, setTableData] = useState<DetectionSummaryInsights[]>([]);
  const [filters, setFilters] = useState<EntityTableFilters[] | null>(null);
  // Fix start/end to always be last 7 days from now, and only update on mount
  const endRef = useRef(moment().unix());
  const startRef = useRef(moment().subtract(7, 'days').unix());

  // Pagination hook - use page size of 5 to match HighestRiskEntitiesTable
  const {
    sortBy,
    offset,
    pageSize,
    pageIndex,
    pageCount,
    onFetchData,
    getPageCount,
    getSortsForAPI,
  } = useManualPagination<DetectionSummaryInsights>({
    initialFetch,
    sortBy: DEFAULT_SORT,
    initPageSize: 5,
  });
  const { isNewSearch } = useIsNewSearch({
    filters,
  });

  const variables = useMemo(() => {
    // Ensure we always have at least one sort parameter
    // If sortBy is empty, default to sorting by max_severity desc
    const sort =
      sortBy.length > 0
        ? getSortsForAPI(sortBy)
        : [{ sort_by: 'max_severity', sort_dir: 'desc' }];

    return {
      start: startRef.current,
      end: endRef.current,
      offset: isNewSearch ? 0 : offset,
      size: pageSize,
      sort,
      tenant: orgTenantsQueryParam?.[0], // Use first tenant or undefined
      include_ai_insights: agenticTriageEnabled,
    };
  }, [
    sortBy,
    getSortsForAPI,
    isNewSearch,
    offset,
    pageSize,
    orgTenantsQueryParam,
    agenticTriageEnabled,
  ]);

  const { data, loading, error, refetch } =
    useGetTopDetectionEntitiesInsightsPaginated({
      notifyOnNetworkStatusChange: true,
      // Skip if no tenant
      skip: !orgTenantsQueryParam?.length,
      variables,
      onError: (error: ApolloError) => {
        // Log errors in development for debugging
        if (process.env.NODE_ENV === 'development') {
          console.error('[EntityTableContext] Query error:', error);
        }
      },
    });

  // Update state when data changes - use useEffect instead of onCompleted
  // to ensure updates happen during proper React lifecycle
  useEffect(() => {
    if (data?.getTopDetectionEntitiesInsightsPaginated) {
      setInitialFetch(false);
      getPageCount(data.getTopDetectionEntitiesInsightsPaginated);
      setTableData(
        data.getTopDetectionEntitiesInsightsPaginated.summary_insights,
      );
    }
  }, [data, getPageCount]);

  const handleSetFilters = useCallback(
    (newFilters: EntityTableFilters[] | null) => {
      setFilters(newFilters);
      if (newFilters?.length) {
        refetch();
      }
    },
    [refetch],
  );

  const value = useMemo(
    () => ({
      tableData,
      totalItems:
        data?.getTopDetectionEntitiesInsightsPaginated?.total_items || 0,
      isLoading: loading,
      error,
      pageIndex,
      pageSize,
      pageCount,
      offset,
      onFetchData,
      filters,
      setFilters: handleSetFilters,
      sortBy,
      refetchTable: refetch,
    }),
    [
      tableData,
      data,
      loading,
      error,
      pageIndex,
      pageSize,
      pageCount,
      offset,
      onFetchData,
      filters,
      handleSetFilters,
      sortBy,
      refetch,
    ],
  );

  return (
    <EntityTableContextInstance.Provider value={value}>
      {children}
    </EntityTableContextInstance.Provider>
  );
};

export function useDetectionsByEntityIndex(entityIndex: number) {
  const context = useEntityTableContext();

  if (!context) throw new Error('Must be used within EntityTableContext');

  const { tableData } = context;
  const tenant_entity = tableData[entityIndex]?.tenant_entity || '';

  const { data } = useGetTopDetectionsByDetectionGroupsPerEntity({
    variables: {
      tenant_entity,
      group_by: DetectionGroups.SeverityLevels,
      size: 3,
    },
    skip: !tenant_entity,
  });

  return (data?.getTopDetectionsByDetectionGroupsPerEntity?.data ??
    []) as DetectionsByDetectionGroupPerEntity[];
}

export const useEntityTableContext = () => {
  const context = useContext(EntityTableContextInstance);
  if (!context) {
    throw new Error('useEntityTable must be used within EntityTableContext');
  }
  return context;
};
